import React, { useState, useEffect, useCallback } from "react";
import { get, post, patch, del } from "../../../helpers/api_helper";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import RichEditor from "../../../components/Common/RichEditor";
import {
  Card, CardBody, Nav, NavItem, NavLink, TabContent, TabPane,
  Button, Spinner, Row, Col, Alert,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import PageHint from "../../../components/Common/PageHint";

/**
 * Çok dilli metin sayfası düzenleyicisi (Hakkımızda / Üretim / KVKK).
 *
 * Hepsi aynı desende çalışıyor: Türkçe orijinal tek satırlık bir tabloda,
 * çeviriler ayrı bir "_Lang" tablosunda. Tek fark bazı tabloların metni
 * çift base64 (OData binary) tutması; bu `trBinary` ile belirtiliyor.
 */
const ODATA_BASE = "https://buharaapi.stechomeyazilim.info";

const decodeBinary = (raw) => {
  if (!raw) return "";
  try { return decodeURIComponent(escape(atob(atob(raw)))); } catch (e) { /* devam */ }
  try { return decodeURIComponent(escape(atob(raw))); } catch (e) { /* devam */ }
  return raw;
};
const encodeBinary = (html) => btoa(btoa(unescape(encodeURIComponent(html || ""))));

const LANGS = [
  { code: "tr", label: "🇹🇷 Türkçe" },
  { code: "en", label: "🇬🇧 English" },
  { code: "fr", label: "🇫🇷 Français" },
  { code: "de", label: "🇩🇪 Deutsch" },
  { code: "nl", label: "🇳🇱 Nederlands" },
];

export default function RichTextPage({
  pageTitle,        // sekme başlığı ve breadcrumb
  description,      // bu ekran neyi yönetiyor
  where,            // sitede nerede görünüyor
  links = [],       // canlı sayfa bağlantıları
  note,             // ek uyarı (isteğe bağlı)
  trTable,          // ör. "Production"
  trField,          // ör. "Production_Html"
  trBinary = false, // Türkçe metin çift base64 mi tutuluyor
  langTable,        // ör. "Production_Lang"
  langField,        // ör. "Production_Html"
}) {
  document.title = `${pageTitle} | BUHARA - Admin Paneli`;

  const [activeTab, setActiveTab] = useState("tr");
  const [records, setRecords]   = useState({});
  const [htmlMap, setHtmlMap]   = useState({});
  const [trRecord, setTrRecord] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [notReady, setNotReady] = useState(false);

  // onlyLang verilirse (kaydetme/silme sonrası) yalnızca o dilin metni
  // tazelenir; diğer sekmelerde henüz kaydedilmemiş metinler korunur.
  const fetchRecords = useCallback(async (onlyLang = null) => {
    if (!onlyLang) setLoading(true);
    let missing = false;
    const recMap = {};
    const hMap = {};
    try {
      const res = await get(`${ODATA_BASE}/${langTable}`);
      (res?.value || []).forEach((item) => {
        recMap[item.Lang_Code] = item;
        hMap[item.Lang_Code] = item[langField] || "";
      });
    } catch (err) {
      console.error(`${langTable} fetch error:`, err);
      missing = true;
    }
    try {
      const trRes = await get(`${ODATA_BASE}/${trTable}`);
      const trItem = (trRes?.value || [])[0] || null;
      setTrRecord(trItem);
      hMap.tr = trItem ? (trBinary ? decodeBinary(trItem[trField]) : trItem[trField] || "") : "";
    } catch (err) {
      console.error(`${trTable} fetch error:`, err);
      missing = true;
    }
    setRecords(recMap);
    setHtmlMap(prev => (onlyLang ? { ...prev, [onlyLang]: hMap[onlyLang] || "" } : hMap));
    setNotReady(missing);
    setLoading(false);
  }, [trTable, trField, trBinary, langTable, langField]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const handleSave = async (lang) => {
    setSaving(true);
    try {
      if (lang === "tr") {
        const value = trBinary ? encodeBinary(htmlMap.tr || "") : (htmlMap.tr || "");
        const payload = { [trField]: value };
        if (trRecord?.ID) {
          await patch(`${ODATA_BASE}/${trTable}(${trRecord.ID})`, payload);
        } else {
          await post(`${ODATA_BASE}/${trTable}`, payload);
        }
        toastr.success("Türkçe metin kaydedildi.");
        await fetchRecords("tr");
        return;
      }
      const existing = records[lang];
      const payload = { Lang_Code: lang, [langField]: htmlMap[lang] || "" };
      if (existing?.ID) {
        await patch(`${ODATA_BASE}/${langTable}(${existing.ID})`, payload);
      } else {
        await post(`${ODATA_BASE}/${langTable}`, payload);
      }
      toastr.success(`${lang.toUpperCase()} çevirisi kaydedildi.`);
      await fetchRecords(lang);
    } catch (err) {
      console.error(err);
      toastr.error("Kayıt sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (lang) => {
    if (lang === "tr") return;
    const existing = records[lang];
    if (!existing?.ID) return;
    if (!window.confirm(`${lang.toUpperCase()} çevirisini silmek istiyor musunuz?`)) return;
    setSaving(true);
    try {
      await del(`${ODATA_BASE}/${langTable}(${existing.ID})`);
      toastr.success(`${lang.toUpperCase()} çevirisi silindi.`);
      setHtmlMap((prev) => ({ ...prev, [lang]: "" }));
      await fetchRecords(lang);
    } catch {
      toastr.error("Silme sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Yönetim" breadcrumbItem={pageTitle} />
        <Row>
          <Col xs="12">
            <Card>
              <CardBody>
                <PageHint what={description} where={where} links={links} note={note} />

                {notReady && (
                  <Alert color="warning" className="mb-3">
                    Bu sayfanın tabloları veritabanında henüz yok.{" "}
                    <strong>BuharaOdata/Kvkk_Production_Certifica.sql</strong> dosyasını
                    çalıştırıp OData servisini yeniden yayınlamanız gerekiyor.
                  </Alert>
                )}

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="warning" />
                    <span className="ms-2">Yükleniyor...</span>
                  </div>
                ) : (
                  <>
                    <Nav tabs className="mb-3">
                      {LANGS.map((l) => (
                        <NavItem key={l.code}>
                          <NavLink
                            className={activeTab === l.code ? "active fw-bold" : ""}
                            onClick={() => setActiveTab(l.code)}
                            style={{ cursor: "pointer" }}
                          >
                            {l.label}
                            {(l.code === "tr" ? trRecord : records[l.code]) && (
                              <span className="ms-1 badge bg-success" style={{ fontSize: 9 }}>✓</span>
                            )}
                          </NavLink>
                        </NavItem>
                      ))}
                    </Nav>

                    <TabContent activeTab={activeTab}>
                      {LANGS.map((l) => (
                        <TabPane tabId={l.code} key={l.code}>
                          <p className="text-muted mb-2">
                            {l.code === "tr"
                              ? "🇹🇷 Türkçe — sitedeki ana metin."
                              : `${l.label} — çeviri metni.`}
                            {(l.code === "tr" ? trRecord : records[l.code])
                              ? " (Mevcut kayıt güncelleniyor)"
                              : " (Yeni kayıt eklenecek)"}
                          </p>
                          <RichEditor
                            value={htmlMap[l.code] || ""}
                            onChange={(data) => setHtmlMap((prev) => ({ ...prev, [l.code]: data }))}
                          />
                          <div className="d-flex gap-2 mt-3">
                            <Button color="warning" disabled={saving} onClick={() => handleSave(l.code)}>
                              {saving ? <Spinner size="sm" /> : null}{" "}
                              {(l.code === "tr" ? trRecord : records[l.code]) ? "Güncelle" : "Kaydet"}
                            </Button>
                            {l.code !== "tr" && records[l.code] && (
                              <Button color="danger" outline disabled={saving} onClick={() => handleDelete(l.code)}>
                                Sil
                              </Button>
                            )}
                          </div>
                        </TabPane>
                      ))}
                    </TabContent>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}
